import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { fabric } from 'fabric';

import { NOOP, NOOP_ASYNC } from '~/NOOP';
import { PickAttributes } from '~/types';

export type AnnotationMode = 'none' | 'text' | 'rect' | 'eraser' | 'draw';

export const ANNOTATION_COLORS = {
  red: '#E33F29',
  orange: '#DC7D29',
  green: '#38B359',
  black: '#222222',
};
export const ANNOTATION_STROKES = {
  small: 2,
  medium: 4,
  large: 6,
};

export interface ICanvasContext {
  annotationEnabled?: boolean;
  mode: AnnotationMode;
  color: keyof typeof ANNOTATION_COLORS;
  stroke: keyof typeof ANNOTATION_STROKES;
  zoom: number;
  minZoom: number;
  maxZoom: number;
  update: (context: Partial<PickAttributes<ICanvasContext>>) => void;
  clear: (ref: React.MutableRefObject<fabric.Canvas>) => void;
  getBlob: (ref: React.MutableRefObject<fabric.Canvas> | React.MutableRefObject<HTMLCanvasElement>) => Promise<Blob | void>;
}

export const defaultContext: ICanvasContext = {
  annotationEnabled: false,
  mode: 'none',
  color: 'red',
  stroke: 'small',
  zoom: 1,
  minZoom: 1,
  maxZoom: 8,
  update: NOOP,
  clear: NOOP,
  getBlob: NOOP_ASYNC,
};

const CanvasContext = createContext<ICanvasContext>(defaultContext);

interface ICanvasProvider {
  zoomConfig?: {
    minZoom: number;
    maxZoom: number;
  },
  children: React.ReactNode;
}

export const CanvasProvider = React.memo<ICanvasProvider>(function CanvasProvider(props) {
  const { zoomConfig, children } = props;
  const clear = useCallback((ref: React.MutableRefObject<fabric.Canvas>) => {
    const { current: canvas } = ref;
    canvas?.getObjects()?.forEach((obj) => {
      if (!obj.isType('image')) {
        canvas.remove(obj);
      }
    });
  }, []);

  const getBlob = useCallback(async (ref: React.MutableRefObject<fabric.Canvas> | React.MutableRefObject<HTMLCanvasElement>) => {
    const { current } = ref;
    const isHTMLCanvas = !!(current as HTMLCanvasElement).toBlob;
    const canvas = isHTMLCanvas ? current as HTMLCanvasElement : (ref.current as fabric.Canvas).getElement();
    if (!isHTMLCanvas) {
      (current as fabric.Canvas).discardActiveObject().renderAll();
    }
    return new Promise<Blob>((resolve) => {
      canvas.toBlob(async (blob: Blob) => {
        if (!isHTMLCanvas) {
          clear(ref as React.MutableRefObject<fabric.Canvas>);
        }
        resolve(blob);
      });
    });
  }, [clear]);

  const state = useState({ ...defaultContext, clear, getBlob });
  const [context, dispatch] = state;

  const update = useCallback((params: Partial<PickAttributes<ICanvasContext>>) => {
    dispatch((prev) => {
      return { ...prev, ...params };
    });
  }, [dispatch]);

  context.update = update;

  useEffect(() => {
    update({ mode: 'none', annotationEnabled: false, zoom: zoomConfig?.minZoom || 1, ...zoomConfig });
  }, [zoomConfig, update]);

  return (
    <CanvasContext.Provider value={context}>
      {children}
    </CanvasContext.Provider>
  );
});

export const useCanvas = () => useContext(CanvasContext);
